import { ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Outlet, useLocation } from 'react-router';
import App404Refresh from './App404Refresh';
import DefaultHelmet from './DefaultHelmet';

// Enhanced error reporting utility
const reportError = (error: Error, errorInfo: any) => {
  // Sanitize error data to prevent sensitive information exposure
  const sanitizedError = {
    message: error.message,
    stack: error.stack,
    name: error.name,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    pathname: window.location.pathname,
    // Add more context as needed
    errorInfo: {
      componentStack: errorInfo?.componentStack,
    }
  };

  // Log to console for development
  console.error('ErrorBoundary caught an error:', sanitizedError);

  // TODO: Integrate with error tracking service (Sentry, LogRocket, etc.)
  // Example: Sentry.captureException(error, { extra: sanitizedError });
  
  // Store error in localStorage for debugging (temporary solution)
  try {
    const existingErrors = JSON.parse(localStorage.getItem('app-errors') || '[]');
    existingErrors.push(sanitizedError);
    // Keep only last 10 errors to prevent storage bloat
    const recentErrors = existingErrors.slice(-10);
    localStorage.setItem('app-errors', JSON.stringify(recentErrors));
  } catch (e) {
    console.warn('Failed to store error in localStorage:', e);
  }
};

function RootLayout({ children }: { children?: ReactNode }) {
  const location = useLocation();

  return (
    <ErrorBoundary
      FallbackComponent={App404Refresh}
      onError={(error, errorInfo) => {
        // Enhanced error reporting with context
        reportError(error, errorInfo);
      }}
      onReset={() => {
        // Clear stored errors on reset
        try {
          localStorage.removeItem('app-errors');
        } catch (e) {
          console.warn('Failed to clear stored errors:', e);
        }
      }}
    >
      <DefaultHelmet />

      {children || <Outlet />}
    </ErrorBoundary>
  );
}

export default RootLayout;
